import Cropper from 'cropperjs';
import { useDispatch } from 'react-redux';
import { useCallback, useMemo } from 'react';

import { getUploadedImageContent, uploadImage } from '../../../../attachments/attachmentActions';
import { updateElement } from '../../../../actions/elementActions';
import { IMAGE_TYPES } from '../../../../../../common/media/mediaConstants';
import { getCroppedImageBlobFromUrl } from '../utils/imageModalImageBlobUtils';
import getFilenameFromUrl from '../../../../../../common/files/getFilenameFromUrl';
import { appendToFilename, enrichBlobAsUploadFile } from '../../imageModalFileUtils';
import type { GenericReduxDispatch } from '../../../../../../common/actions/actionTypes';
import {
    buildBackgroundRemovalRenderedChanges,
    buildBackgroundRemovalCropChanges,
    buildBackgroundRemovalRevertChanges,
    getActiveBackgroundRemovalVariant,
    getRevertedImage,
    hasBackgroundRemovalCache,
    BackgroundRemovalVariantOptions,
} from '../../backgroundRemoval/imageModalBackgroundRemovalUtils';
import {
    dispatchBackgroundRemovalImageUpload,
    type BackgroundRemovalUploadResult,
} from '../../backgroundRemoval/imageModalBackgroundRemovalService';
import { getBackgroundRemovalCropImageData } from './backgroundRemovalCropUtils';
import type {
    ImMNElement,
    MNImageContent,
    MNImageEditorDataContent,
} from '../../../../../../common/elements/elementModelTypes';
import type { DrawingTransform } from '../../utils/imageDrawingTransformUtils';

type UseBackgroundRemovalCropOptions = {
    element: ImMNElement;
    elementId: string;
    useSecureMediaUrl: boolean;
    renderSecureImagesThroughCF: boolean;
    // Mark plain edit uploads so the board canvas holds the previous image while the upload is in flight
    // instead of jumping to the local preview ahead of the modal (V2 behaviour; V1 keeps the old timing).
    markUploadAsEditPreview?: boolean;
};

/** Crops a background-removal variant source in the persisted (full-source) coordinate space. */
const createVariantCroppedBlob = (
    variantImageUrl: string | null,
    variantImageType: string,
    imageEditorData: Cropper.Data,
): Promise<Blob | null> => {
    if (!variantImageUrl) return Promise.resolve(null);

    return getCroppedImageBlobFromUrl({
        imageUrl: variantImageUrl,
        inputImageType: variantImageType,
        outputImageType: variantImageType,
        imageEditorData,
    });
};

/**
 * Encapsulates background-removal state management during crop/rotate operations.
 */
export const useBackgroundRemovalCrop = ({
    element,
    elementId,
    useSecureMediaUrl,
    renderSecureImagesThroughCF,
    markUploadAsEditPreview = false,
}: UseBackgroundRemovalCropOptions) => {
    const dispatch = useDispatch<GenericReduxDispatch>();

    const hasBgRemovalCache = hasBackgroundRemovalCache(element);
    const activeVariant = getActiveBackgroundRemovalVariant(element);
    const activeVariantIsTransparent = activeVariant === BackgroundRemovalVariantOptions.withoutBackground;

    const { backgroundRemovalEditSource, inactiveVariantImageUrl, inactiveVariantImageType, imageType } = useMemo(
        () =>
            getBackgroundRemovalCropImageData({
                element,
                elementId,
                useSecureMediaUrl,
                renderSecureImagesThroughCF,
            }),
        [element, elementId, useSecureMediaUrl, renderSecureImagesThroughCF],
    );

    const shouldIgnoreEditedImageFileSizeLimits = !!backgroundRemovalEditSource;

    const uploadCroppedBackgroundRemovalImage = useCallback(
        async (id: string, file: Blob) => {
            const result = await dispatchBackgroundRemovalImageUpload(dispatch, {
                elementId: id,
                file,
                ignoreFileSizeLimits: true,
            });

            if (!result) return result;
            const uploadedImage = getUploadedImageContent({
                ...result,
                transparent: activeVariantIsTransparent,
            }) as MNImageContent;

            dispatch(
                updateElement({
                    id,
                    changes: {
                        image: uploadedImage,
                        backgroundRemoval: buildBackgroundRemovalRenderedChanges({
                            element,
                            uploadedImage,
                            variant: activeVariant,
                        }),
                    },
                }),
            );

            return uploadedImage;
        },
        [activeVariant, activeVariantIsTransparent, dispatch, element],
    );

    const uploadEditedImage = useCallback(
        (id: string, file: Blob) => {
            if (hasBgRemovalCache) return uploadCroppedBackgroundRemovalImage(id, file);

            return dispatch(
                uploadImage({
                    id,
                    file,
                    attachmentData: undefined,
                    imageType: IMAGE_TYPES.ELEMENT,
                    options: {
                        ignoreFileSizeLimits: shouldIgnoreEditedImageFileSizeLimits,
                        isEditPreview: markUploadAsEditPreview,
                    },
                }),
            );
        },
        [
            dispatch,
            hasBgRemovalCache,
            markUploadAsEditPreview,
            shouldIgnoreEditedImageFileSizeLimits,
            uploadCroppedBackgroundRemovalImage,
        ],
    );

    /**
     * Mirror the active crop onto the hidden variant so toggling remains aligned after save.
     */
    const createAdditionalCroppedImageBlob = useCallback(
        (imageEditorData: Cropper.Data): Promise<Blob | null> =>
            createVariantCroppedBlob(inactiveVariantImageUrl, inactiveVariantImageType, imageEditorData),
        [inactiveVariantImageUrl, inactiveVariantImageType],
    );

    /**
     * Store the mirrored hidden-side crop without changing which variant is currently active.
     *
     * `sourceElement` lets callers working ahead of a React re-render (e.g. staging immediately after the
     * removal cache was dispatched) build the rendered changes from the store's current element — the
     * changes replace the whole `backgroundRemoval` object, so a stale prop element would wipe the
     * just-written source cache.
     */
    const uploadAdditionalCroppedImage = useCallback(
        async ({
            file,
            activeRenderedImage,
            sourceElement = element,
        }: {
            file: Blob;
            activeRenderedImage?: MNImageContent;
            sourceElement?: ImMNElement;
        }) => {
            const sourceActiveVariant = getActiveBackgroundRemovalVariant(sourceElement);
            const sourceInactiveVariant =
                sourceActiveVariant === BackgroundRemovalVariantOptions.withoutBackground
                    ? BackgroundRemovalVariantOptions.withBackground
                    : BackgroundRemovalVariantOptions.withoutBackground;

            const result: BackgroundRemovalUploadResult | undefined = await dispatchBackgroundRemovalImageUpload(
                dispatch,
                {
                    elementId,
                    file,
                    ignoreFileSizeLimits: true,
                },
            );

            if (!result) return result;
            const uploadedImage = getUploadedImageContent({
                ...result,
                transparent: sourceInactiveVariant === BackgroundRemovalVariantOptions.withoutBackground,
            }) as MNImageContent;

            dispatch(
                updateElement({
                    id: elementId,
                    changes: {
                        backgroundRemoval: buildBackgroundRemovalRenderedChanges({
                            element: sourceElement,
                            uploadedImage,
                            variant: sourceInactiveVariant,
                            additionalRenderedImage: activeRenderedImage,
                            additionalRenderedVariant: sourceActiveVariant,
                        }),
                    },
                }),
            );

            return uploadedImage;
        },
        [dispatch, element, elementId],
    );

    /**
     * Create the inactive rendered variant in the current crop coordinate space before it is previewed.
     *
     * `currentElement` must be the store's current element when staging runs ahead of a React re-render:
     * on the first removal the source cache was dispatched a moment earlier, so the hook's prop element
     * does not have it yet — deriving the variant URL from the prop would silently bail here and leave
     * the removal toast waiting on a preview swap that never happens.
     */
    const createInactiveRenderedVariant = useCallback(
        async (imageEditorData: Cropper.Data, currentElement: ImMNElement = element): Promise<boolean> => {
            const { inactiveVariantImageUrl: currentInactiveUrl, inactiveVariantImageType: currentInactiveType } =
                getBackgroundRemovalCropImageData({
                    element: currentElement,
                    elementId,
                    useSecureMediaUrl,
                    renderSecureImagesThroughCF,
                });
            if (!currentInactiveUrl) return false;

            const blob = await createVariantCroppedBlob(currentInactiveUrl, currentInactiveType, imageEditorData);
            if (!blob) return false;

            const filename = appendToFilename(getFilenameFromUrl(currentInactiveUrl, currentInactiveType));
            const uploadedImage = await uploadAdditionalCroppedImage({
                file: enrichBlobAsUploadFile(blob, filename),
                sourceElement: currentElement,
            });

            return !!uploadedImage;
        },
        [element, elementId, renderSecureImagesThroughCF, uploadAdditionalCroppedImage, useSecureMediaUrl],
    );

    /**
     * Persist crop metadata while preserving the cached background-removal sources.
     */
    const buildCropSaveChanges = useCallback(
        (
            imageOriginal: MNImageContent,
            imageEditorData: MNImageEditorDataContent,
            clearDrawing: boolean,
            drawingSessionTransform?: DrawingTransform,
        ) =>
            buildBackgroundRemovalCropChanges({
                element,
                imageOriginal,
                imageEditorData,
                clearDrawing,
                preserveBackgroundRemoval: hasBgRemovalCache,
                drawingSessionTransform,
            }),
        [hasBgRemovalCache, element],
    );

    /**
     * Revert crop metadata back to the full source image for the currently active variant.
     */
    const buildRevertChanges = useCallback(
        (imageOriginal: MNImageContent, clearDrawing: boolean) =>
            buildBackgroundRemovalRevertChanges({
                element,
                imageOriginal,
                clearDrawing,
                preserveBackgroundRemoval: hasBgRemovalCache,
            }),
        [hasBgRemovalCache, element],
    );

    const updateElementImageData = useCallback(
        (
            id: string,
            imageOriginal: MNImageContent,
            imageEditorData: MNImageEditorDataContent,
            clearDrawing: boolean,
            drawingSessionTransform?: DrawingTransform,
        ) =>
            dispatch(
                updateElement({
                    id,
                    changes: buildCropSaveChanges(
                        imageOriginal,
                        imageEditorData,
                        clearDrawing,
                        drawingSessionTransform,
                    ),
                    silent: true,
                    sync: true,
                }),
            ),
        [dispatch, buildCropSaveChanges],
    );

    const revertToOriginalImageData = useCallback(
        (id: string, imageOriginal: MNImageContent, clearDrawing: boolean) =>
            dispatch(
                updateElement({
                    id,
                    changes: buildRevertChanges(imageOriginal, clearDrawing),
                    silent: true,
                    sync: true,
                }),
            ),
        [dispatch, buildRevertChanges],
    );

    const getRevertImage = useCallback(() => getRevertedImage(element), [element]);

    return {
        backgroundRemovalEditSource,
        hasBackgroundRemovalCache: hasBgRemovalCache,
        imageType,
        uploadEditedImage,
        createAdditionalCroppedImageBlob,
        uploadAdditionalCroppedImage,
        createInactiveRenderedVariant,
        updateElementImageData,
        revertToOriginalImageData,
        getRevertImage,
    };
};
